<template>
  <v-card flat>
    <v-card-title class="title font-weigth-light px-0 py-0 mb-4">
      User Booking Details
    </v-card-title>
    <v-card-text class="px-0">
      <v-row>
        <v-col cols="4">
          <date-picker
            v-model="datePicker.start"
            :masks="datePicker.masks"
            :min-date="datePicker.minimum"
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                :value="inputValue"
                label="From Date"
                v-on="inputEvents"
                outlined
                clearable
                :rules="[
                  v => {
                    if (!datePicker.end) return true;
                    return !!v || 'Start Date Required';
                  }
                ]"
                hide-details
              />
            </template>
          </date-picker>
        </v-col>
        <v-col cols="4">
          <date-picker
            v-model="datePicker.end"
            :masks="datePicker.masks"
            :min-date="datePicker.start"
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                :value="inputValue"
                label="To Date"
                v-on="inputEvents"
                outlined
                clearable
                :rules="[
                  v => {
                    if (!datePicker.end) return true;
                    return !!v || 'End Date Required';
                  }
                ]"
                hide-details
              />
            </template>
          </date-picker>
        </v-col>
        <v-col cols="4">
          <v-combobox
            v-model="selectedUser"
            :items="users"
            :item-text="item => `${item.first_name} ${item.last_name}`"
            item-value="id"
            label="User"
            multiple
            outlined
            required
            @change="(items) => { changeUser(items); checkAllUser(false) }"
          >
            <template #prepend-item>
              <v-list-item
                ripple
                @click="checkAllUser(true)"
              >
                <v-list-item-action class="mr-3">
                  <v-icon>{{ iconCheckAllUser }}</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ textCheckAllUser }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2" />
            </template>
            <template #selection="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.first_name }} {{ item.last_name }}</span>
              </v-chip>
              <span
                v-if="index === 1 && selectedUser.length > 1"
                class="grey--text caption"
              >
                (+{{ selectedUser.length - 1 }} Others)&nbsp;
              </span>
            </template>
          </v-combobox>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          class="py-0 d-flex justify-end"
        >
          <v-btn
            border
            color="primary"
            @click="generateReport"
          >
            Generate
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-if="isGenerate && selectedUserForTabs.length">
        <v-col cols="12">
          <v-tabs
            bg-color="primary"
            v-model="selectedTab"
          >
            <v-tab
              v-for="tab in selectedUserForTabs"
              :key="tab.id"
              :value="tab.id"
              @click="generateReport(tab.id)"
            >
              {{ tab.first_name }} {{ tab.last_name }}
            </v-tab>
          </v-tabs>
          <v-data-table
            :headers="headers"
            :items="dataPerTab"
            class="font-weight-light pa-5"
            :loading="isLoading"
            loading-text="Loading"
            :footer-props="footerProps"
            :options.sync="paginationOptions"
          >
            <template #[`item.book_detail.start_geofence_id`]="{ item }">
              {{ getOriginName(item.book_detail) }}
            </template>
            <template #[`item.book_detail.vehicle_name`]="{ item }">
              {{ getVehicleName(item.book_detail) }}
            </template>
            <template #[`item.book_detail.end_geofence_id`]="{ item }">
              {{ getDestinationName(item.book_detail) }}
            </template>
            <template #[`item.book_detail.start_datetime`]="{ item }">
              {{ startDateFormat(item.book_detail) }}
            </template>
            <template #[`item.book_detail.end_datetime`]="{ item }">
              {{ endDateFormat(item.book_detail) }}
            </template>
            <template #[`item.book_detail.created_at`]="{ item }">
              {{ formatDate(item.book_detail.created_at) }}
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-row v-if="isLoading && selectedUserForTabs.length === 0">
        <v-col cols="12">
          <p class="text-center">
            Loading
          </p>
        </v-col>
      </v-row>
      <v-row v-if="isGenerate && !isLoading && selectedUserForTabs.length === 0">
        <v-col cols="12">
          <p class="text-center">
            No Data
          </p>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from "moment";
import isUndefined from "lodash/isUndefined";
import isNull from "lodash/isNull";
import get from "lodash/get";

export default {
  data() {
    return {
      users: [],
      assetTypes: [],
      geofences: [],
      selectedUser: [],
      selectedUserForTabs: [],
      datePicker: {
        start: '',
        startUnix: '',
        paramStart: '',
        end: '',
        endUnix: '',
        paramEnd: '',
        masks: {
          input: 'DD MMMM YYYY'
        },
      },
      selectedTab: '',
      headers: [
        { text: "Booking ID", value: "book_detail.id" },
        { text: 'First Name', align: 'left', value: 'first_name' },
        { text: 'Last Name', align: 'left', value: 'last_name' },
        { text: "From", value: "book_detail.start_geofence_id" },
        { text: "To", value: "book_detail.end_geofence_id" },
        { text: "Start Date", value: "book_detail.start_datetime" },
        { text: "End Date", value: "book_detail.end_datetime" },
        { text: "Asset Name", value: "book_detail.vehicle_name" },
        { text: "Created At", value: "book_detail.created_at"},
      ],
      footerProps: {
        itemsPerPageOptions: [2, 3, 5, 10, 20, 50, 100, 200, 500]
      },
      paginationOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: []
      },
      data: [],
      isLoading: false,
      isGenerate: false,
      timeout: 0,
      textCheckAllUser: 'CHECK ALL USER',
      iconCheckAllUser: 'mdi-check-all',
    };
  },
  props: {
    params: {
      type: Object,
      required: false,
      default: () => ({
        users: [],
        assetTypes: [],
        geofences: [],
      }),
    },
  },
  mounted() {
    this.users = this.params.users;
    this.assetTypes = this.params.assetTypes;
    this.geofences = this.params.geofences;
  },
  watch: {
    "datePicker.start"(value) {
      const startDate = moment(value).format("YYYY-MM-DD");
      this.datePicker.paramStart = startDate;
    },
    "datePicker.end"(value) {
      const endDate = moment(value).format("YYYY-MM-DD");
      this.datePicker.paramEnd = endDate;
    },
  },
  computed: {
    dataPerTab() {
      if (!this.selectedUserForTabs.length) return [];
      const user = this.selectedUserForTabs[this.selectedTab];
      return this.data.filter((item) => item.user_id === user.id);
    },
  },
  methods: {
    checkAllUser(force = false) {
      if (force) {
        if (this.textCheckAllUser === 'CHECK ALL USER') {
          this.selectedUser = this.users;
          this.textCheckAllUser = 'UNCHECK ALL USER';
          this.iconCheckAllUser = 'mdi-close';
        } else {
          this.selectedUser = [];
          this.textCheckAllUser = 'CHECK ALL USER';
          this.iconCheckAllUser = 'mdi-check-all';
        }
      } else {
        if (this.selectedUser.length === this.users.length) {
          this.textCheckAllUser = 'UNCHECK ALL USER';
          this.iconCheckAllUser = 'mdi-close';
        } else {
          this.textCheckAllUser = 'CHECK ALL USER';
          this.iconCheckAllUser = 'mdi-check-all';
        }
      }
    },
    changeUser(items) {
      this.selectedUser = items.filter((user) => typeof user === 'object' && user.id);
    },
    generateReport(userId) {
      if (!this.selectedUser.length || !this.datePicker.start || !this.datePicker.end) return;
      if (this.selectedUser.length && this.datePicker.start && this.datePicker.end) {
        let selectedUserId = null;
        if (typeof userId !== 'object') {
          selectedUserId = userId;
        }
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          this.isLoading = true;
          const promise = selectedUserId == null ? this.getDataBookTabs() : Promise.resolve();
          promise.then(() => {
            if (selectedUserId == null) {
              if (this.selectedUserForTabs.length) {
                this.selectedTab = 0;
                this.getDataBook(this.selectedUserForTabs[0].id);
              } else {
                this.data = [];
                this.isGenerate = true;
                this.isLoading = false;
              }
            } else {
              this.getDataBook(selectedUserId);
            }
          })
        }, 500);
      }
    },
    getDataBookTabs() {
      return this.$store.dispatch('API_CALL', {
        $method: 'get_book_for_tabs',
        $postData: {
          user_id: this.selectedUser.map((user) => user.id),
          clientid: this.$store.state.clientid,
          start_date: this.datePicker.paramStart,
          end_date: this.datePicker.paramEnd,
          get_schedules: 1,
          get_recurring: 0,
        },
      })
      .then((response) => {
        this.selectedUserForTabs = response.data.data.map((user) => this.selectedUser.find((u) => u.id === user.user_id));
      });
    },
    getDataBook(userId) {
      return this.$store.dispatch('API_CALL', {
        $method: 'get_all_book',
        $postData: {
          user_id: userId,
          clientid: this.$store.state.clientid,
          start_date: this.datePicker.paramStart,
          end_date: this.datePicker.paramEnd,
          get_schedules: 1,
          get_recurring: 0,
        },
      })
      .then((response) => {
        this.data = response.data.data;
        this.isGenerate = true;
        this.isLoading = false;
      });
    },
    getOriginName(item) {
      let destination = '';
      if (item.start_geofence_id) {
        this.geofences[this.$store.state.clientid]
          .forEach((group) => {
            const geofenceDestination = group.geofences.find(
              (geofence) =>
                parseInt(geofence.Id) === parseInt(item.start_geofence_id)
            );
            if (!isUndefined(geofenceDestination)) {
              destination = geofenceDestination.name;
            }
          });
      } else {
        try {
          const metadata = JSON.parse(item.metadata);
          destination = metadata.hasOwnProperty('address') ? metadata.address : '';
        } catch (e) {}
      }
      return destination;
    },
    getVehicleName(item) {
      if (item == null) return;
      let metadata = '';
      if (!isNull(item.vehicle_name)) {
        metadata = `Vehicle - ${item.vehicle_name}`;
      } else if (!isNull(item.asset_id)) {
        const typeName = this.assetTypes.find((d) => d.id == item.asset_type);
        metadata = `${get(typeName, 'name', '-')} - ${item.asset_number}`;
      } else {
        metadata = '--';
      }
      if (!metadata) return;
      return metadata;
    },
    getDestinationName(item) {
      let destination = '';
      if (item.end_geofence_id) {
        this.geofences[this.$store.state.clientid]
          .forEach((group) => {
            const geofenceDestination = group.geofences.find(
              (geofence) =>
                parseInt(geofence.Id) === parseInt(item.end_geofence_id)
            );
            if (!isUndefined(geofenceDestination)) {
              destination = geofenceDestination.name;
            }
          });
      } else {
        try {
          const metadata = JSON.parse(item.metadata);
          destination = metadata.hasOwnProperty('address') ? metadata.address : '';
        } catch (e) {}
      }
      return destination;
    },
    startDateFormat(item) {
      if (item == null) return;
      let date = null;
      if (item.hasOwnProperty("start_datetime")) {
        date = moment(item.start_datetime).format('ddd, D MMM YYYY HH:mm')
      }
      return date;
    },
    endDateFormat(item) {
      if (item == null) return;
      let date = null;
      if (item.hasOwnProperty("end_datetime")) {
        date = moment(item.end_datetime).format('ddd, D MMM YYYY HH:mm')
      }
      return date;
    },
    formatDate(date, format = 'D MMM YYYY HH:mm') {
      if (date == null) return;
      const dateFormatted = moment(date).format(format)
      return dateFormatted;
    },
  },
};
</script>
